import React, { useEffect, useMemo, useState } from "react";
import TournamentDocuments from "../Tournament/TournamentDocuments";
import ManageEvents from "../Events/ManageEvents";
import TournamentLocations from "../../TournamentLocations/TournamentLocations";
import SquadManagementTab from "./SquadManagementTab";
import SignupSettings from "../../Admin/SignupSettings";
import SetupJumpBar from "./SetupJumpBar";
import TournamentAverageCatalogCard from "../../Tournament/TournamentAverageCatalogCard";
import LaneAssignmentSetupCard from "../../Tournament/LaneAssignmentSetupCard";
import DoublesSinglesSetupCard from "../../Tournament/DoublesSinglesSetupCard";
import BracketProductsSetup from "../../Tournament/BracketProductsSetup";
import { listOrganizationBowlingCenters } from "../../../utility/organizationBowlingCenters";
import {
  FEATURE_AI_LEAGUE_AVERAGE_IMPORT,
  tournamentHasAiFeature,
} from "../../../utility/features";

const TournamentSetupTab = ({
  tournamentId,
  tournament,
  onSetupChange,
}) => {
  const organizationId = tournament?.organizationID;
  const [houseCount, setHouseCount] = useState(null);
  const [eventsRevision, setEventsRevision] = useState(0);
  const [bracketsRevision, setBracketsRevision] = useState(0);
  const [squadsRevision, setSquadsRevision] = useState(0);
  const [catalogAllowed, setCatalogAllowed] = useState(false);

  const handleEventsChange = () => {
    setEventsRevision((value) => value + 1);
    onSetupChange?.();
  };

  useEffect(() => {
    if (!organizationId) {
      setHouseCount(0);
      return;
    }
    let cancelled = false;
    listOrganizationBowlingCenters(organizationId)
      .then((centers) => {
        if (!cancelled) setHouseCount(centers.length);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setHouseCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId || !tournamentId) {
      setCatalogAllowed(false);
      return;
    }
    let cancelled = false;
    tournamentHasAiFeature(
      organizationId,
      FEATURE_AI_LEAGUE_AVERAGE_IMPORT,
      tournamentId
    )
      .then((enabled) => {
        if (!cancelled) setCatalogAllowed(Boolean(enabled));
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setCatalogAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, tournamentId]);

  const sections = useMemo(
    () =>
      [
        { id: "setup-events", label: "Events" },
        { id: "setup-brackets", label: "Brackets" },
        houseCount === 0
          ? { id: "setup-centers", label: "Centers" }
          : null,
        { id: "setup-squads", label: "Squads" },
        { id: "setup-lanes", label: "Lanes" },
        catalogAllowed
          ? { id: "setup-catalog", label: "Average Catalog" }
          : null,
        { id: "setup-signups", label: "Signups" },
        { id: "setup-documents", label: "Documents" },
      ].filter(Boolean),
    [houseCount, catalogAllowed]
  );

  return (
    <div>
      <div className="sticky top-0 z-20 card-board overflow-hidden mb-6">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
            Setup
          </h1>
          <p className="text-sm text-brand-mist mt-1">
            Events, squads, signups, and documents.
          </p>
        </div>
        <SetupJumpBar sections={sections} />
      </div>
      <div className="space-y-6">
        <section id="setup-events" className="scroll-mt-20">
          <ManageEvents
            tournamentId={tournamentId}
            isLoggedIn={true}
            onEventsChange={handleEventsChange}
          />
        </section>
        <section id="setup-brackets" className="scroll-mt-20">
          <BracketProductsSetup
            tournamentId={tournamentId}
            onBracketsChange={() =>
              setBracketsRevision((value) => value + 1)
            }
          />
        </section>
        {houseCount === 0 ? (
          <section id="setup-centers" className="scroll-mt-20">
            <TournamentLocations
              organizationId={organizationId}
              onCentersChange={(centers) => setHouseCount(centers.length)}
            />
          </section>
        ) : null}
        <section id="setup-squads" className="scroll-mt-20">
          <SquadManagementTab
            eventsRevision={eventsRevision}
            onSquadsChange={() => {
              setSquadsRevision((value) => value + 1);
              onSetupChange?.();
            }}
          />
          <DoublesSinglesSetupCard
            tournamentId={tournamentId}
            eventsRevision={eventsRevision}
            squadsRevision={squadsRevision}
          />
        </section>
        <section id="setup-lanes" className="scroll-mt-20">
          <LaneAssignmentSetupCard tournamentId={tournamentId} />
        </section>
        {catalogAllowed ? (
          <section id="setup-catalog" className="scroll-mt-20">
            <TournamentAverageCatalogCard tournament={tournament} />
          </section>
        ) : null}
        <section id="setup-signups" className="scroll-mt-20">
          <SignupSettings
            eventsRevision={eventsRevision}
            bracketsRevision={bracketsRevision}
          />
        </section>
        <section id="setup-documents" className="scroll-mt-20">
          <TournamentDocuments tournamentId={tournamentId} />
        </section>
      </div>
    </div>
  );
};

export default TournamentSetupTab;
